'use client'

import { useCallback, useMemo, useState } from 'react'

type Props = {
  ratio: string
  size: number
  displayLimit: number
  totalItemCount: number
  gap?: number
}

export const useThumbnail = ({
  ratio,
  size,
  displayLimit,
  totalItemCount,
  gap = 12,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(0)

  const itemWidth = useMemo(() => {
    const [widthRatio, heightRatio] = ratio.split(':').map(Number)

    return Math.ceil((size * widthRatio) / heightRatio)
  }, [ratio, size])

  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalItemCount / displayLimit)
    const isFirstPage = currentPage === 0
    const isLastPage = currentPage === totalPages - 1

    return {
      totalPages,
      isFirstPage,
      isLastPage,
    }
  }, [totalItemCount, displayLimit, currentPage])

  const containerWidth = useMemo(() => {
    const totalThumbnailWidth = displayLimit * itemWidth
    const totalGapWidth = Math.max(0, displayLimit - 1) * gap

    return totalThumbnailWidth + totalGapWidth
  }, [displayLimit, itemWidth])

  const currentPageOffset = useMemo(() => {
    if (pagination.isFirstPage) {
      return 0
    }

    const pageWidth = containerWidth + gap

    if (pagination.isLastPage) {
      const previousPagesWidth = (currentPage - 1) * pageWidth
      const remainingImageCount =
        totalItemCount % displayLimit === 0
          ? displayLimit
          : totalItemCount % displayLimit

      const remainingWidth = remainingImageCount * (itemWidth + gap)

      return previousPagesWidth + remainingWidth
    }

    return currentPage * pageWidth
  }, [
    currentPage,
    pagination.isFirstPage,
    pagination.isLastPage,
    totalItemCount,
    displayLimit,
    itemWidth,
    containerWidth,
    gap,
  ])

  const goToPrevPage = useCallback(() => {
    if (pagination.isFirstPage) return

    setCurrentPage(prev => prev - 1)
  }, [pagination.isFirstPage])

  const goToNextPage = useCallback(() => {
    if (pagination.isLastPage) return

    setCurrentPage(prev => prev + 1)
  }, [pagination.isLastPage])

  return {
    itemWidth,
    containerWidth,
    currentPageOffset,
    pagination,
    goToPrevPage,
    goToNextPage,
  }
}
